import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Footer from '../../components/Footer';
import { Award, Download, Eye, Share2, CheckCircle, Star, Calendar, TrendingUp, X, Clock } from 'lucide-react';
import { getUserEnrollments, getSettings, getCourseExam, submitCourseExam, getCourseExamAttempts } from '../../services/api';
import CertificateModal from '../../components/CertificateModal';
import ImageWithFallback from '../../components/ImageWithFallback';

const Certificates = () => {
    const { user, isLoggedIn } = useAuth();
    const navigate = useNavigate();
    const [completedCourses, setCompletedCourses] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [filter, setFilter] = useState('all'); // all, recent, popular
    const [loading, setLoading] = useState(true);

    const [_adminSettings, setAdminSettings] = useState({});

    // Exam states
    const [examCourse, setExamCourse] = useState(null);
    const [examQuestions, setExamQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [examLoading, setExamLoading] = useState(false);
    const [examSubmitting, setExamSubmitting] = useState(false);
    const [examResult, setExamResult] = useState(null);

    // Attempts history states
    const [_attemptsCourse, setAttemptsCourse] = useState(null);
    const [_attemptsList, setAttemptsList] = useState([]);
    const [_attemptsLoading, setAttemptsLoading] = useState(false);

    useEffect(() => {
        if (isLoggedIn === null) return;

        if (isLoggedIn === false) {
            navigate('/');
            return;
        }

        const fetchCertificateData = async () => {
            try {
                const enrollRes = await getUserEnrollments();
                if (enrollRes.success) {
                    setEnrollments(enrollRes.data);
                    const completed = enrollRes.data.filter(course => {
                        const progress = Number(course.progress || 0);
                        return progress >= 99;
                    });
                    setCompletedCourses(completed);
                }

                const settingsRes = await getSettings();
                if (settingsRes.success) {
                    setAdminSettings(settingsRes.data);
                }
            } catch (error) {
                console.error("Error fetching certificate data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCertificateData();
    }, [isLoggedIn, navigate]);

    useEffect(() => {
        if (examCourse && examQuestions.length > 0) {
            localStorage.setItem(
                `exam_progress_course_${examCourse.id}`,
                JSON.stringify({
                    currentIndex: currentQuestionIndex,
                    selectedAnswers: selectedAnswers
                })
            );
        }
    }, [currentQuestionIndex, selectedAnswers, examCourse, examQuestions]);

    const handleDownloadCertificate = (course) => {
        const element = document.createElement('a');
        const text = `Certificate of Completion\n\nThis certifies that ${user?.name}\nhas successfully completed\n${course.title}\n\nDate: ${new Date().toLocaleDateString()}`;
        const file = new Blob([text], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `certificate-${course.title.replace(/\s+/g, '-').toLowerCase()}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    };

    const handleViewCertificate = (course) => {
        setSelectedCertificate(course);
    };

    const handleStartExam = async (course) => {
        setExamCourse(course);
        setExamLoading(true);
        setExamResult(null);

        const savedProgressStr = localStorage.getItem(`exam_progress_course_${course.id}`);
        let savedIndex = 0;
        let savedAnswers = {};
        if (savedProgressStr) {
            try {
                const parsed = JSON.parse(savedProgressStr);
                savedIndex = Number(parsed.currentIndex || 0);
                savedAnswers = parsed.selectedAnswers || {};
            } catch (e) {
                console.error("Error parsing saved exam progress:", e);
            }
        }

        setCurrentQuestionIndex(savedIndex);
        setSelectedAnswers(savedAnswers);
        try {
            const res = await getCourseExam(course.id);
            if (res.success) {
                setExamQuestions(res.data);
            } else {
                alert(res.message || "Failed to load exam questions.");
                setExamCourse(null);
            }
        } catch (error) {
            console.error("Error loading exam:", error);
            alert(error.message || "Error connecting to server. Please try again.");
            setExamCourse(null);
        } finally {
            setExamLoading(false);
        }
    };

    const handleSubmitExam = async () => {
        const answersList = [];
        for (let i = 0; i < examQuestions.length; i++) {
            if (selectedAnswers[i] === undefined) {
                alert("Please answer all questions before submitting.");
                return;
            }
            answersList.push(selectedAnswers[i]);
        }

        setExamSubmitting(true);
        try {
            const res = await submitCourseExam(examCourse.id, answersList);
            if (res.success) {
                setExamResult(res);
                localStorage.removeItem(`exam_progress_course_${examCourse.id}`);
                if (res.passed) {
                    setCompletedCourses(prev =>
                        prev.map(c => c.id === examCourse.id ? { ...c, certificate_earned: true } : c)
                    );
                }
            } else {
                alert(res.message || "Error submitting exam.");
            }
        } catch (error) {
            console.error("Error submitting exam:", error);
            alert(error.message || "Error connecting to server. Please try again.");
        } finally {
            setExamSubmitting(false);
        }
    };

    const handleShareCertificate = (course) => {
        setSelectedCertificate(course);
        setShowShareModal(true);
    };

    const handleViewAttempts = async (course) => {
        setAttemptsCourse(course);
        setAttemptsLoading(true);
        try {
            const res = await getCourseExamAttempts(course.id);
            if (res.success) {
                setAttemptsList(res.data);
            } else {
                alert(res.message || "Failed to load exam attempts.");
                setAttemptsCourse(null);
            }
        } catch (error) {
            console.error("Error loading attempts:", error);
            alert(error.message || "Error connecting to server. Please try again.");
            setAttemptsCourse(null);
        } finally {
            setAttemptsLoading(false);
        }
    };

    const shareToSocial = (platform) => {
        const text = `I just completed ${selectedCertificate?.title}! 🎉`;
        const urls = {
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`
        };
        window.open(urls[platform], '_blank', 'width=600,height=400');
        setShowShareModal(false);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
                <div className="max-w-7xl mx-auto px-2.5 sm:px-6 py-4 w-full animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const earnedCertificates = completedCourses.filter(c => c.certificate_earned);
    const totalCertificates = earnedCertificates.length;
    const totalEnrolled = enrollments.length;
    const completedCount = completedCourses.length;
    const completionRate = totalEnrolled > 0 ? Math.round((completedCount / totalEnrolled) * 100) : 0;
    const coursesWithScores = enrollments.filter(c => Number(c.exam_score) > 0);
    const averageScore = coursesWithScores.length > 0
        ? Math.round(coursesWithScores.reduce((sum, c) => sum + Number(c.exam_score), 0) / coursesWithScores.length)
        : 0;

    const earnedThisMonth = earnedCertificates.filter(c => {
        if (!c.enrolled_at) return false;
        const enrollDate = new Date(c.enrolled_at);
        const now = new Date();
        return enrollDate.getMonth() === now.getMonth() && enrollDate.getFullYear() === now.getFullYear();
    }).length;

    return (
        <div className="min-h-screen bg-gray-50/50 flex flex-col font-sans">
            <main className="flex-1 max-w-7xl mx-auto px-2.5 sm:px-6 lg:px-8 py-3 sm:py-6 w-full space-y-3 sm:space-y-5">

                {/* Header Banner Card (Compact Mobile Height & Font Size) */}
                <div className="bg-gradient-to-r from-emerald-900 via-teal-900 to-gray-900 rounded-xl sm:rounded-2xl p-3 sm:p-6 text-white shadow-xs">
                    <div className="inline-flex items-center gap-1 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-[9px] sm:text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-1">
                        🏆 Student Achievements
                    </div>
                    <h1 className="text-xs sm:text-xl font-bold tracking-tight">My Certificates & Exam Badges</h1>
                    <p className="text-[10px] sm:text-xs text-emerald-100/90 mt-0.5">View, download, and share your earned course certificates.</p>
                </div>

                {/* 2x2 Compact Quick Stats Cards Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3.5">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-2.5 sm:p-4 text-white shadow-2xs">
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wide text-amber-100">Certificates</span>
                            <Award size={14} className="text-amber-200" />
                        </div>
                        <p className="text-sm sm:text-2xl font-extrabold">{totalCertificates}</p>
                    </div>

                    <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-xl p-2.5 sm:p-4 text-white shadow-2xs">
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wide text-emerald-100">Completion</span>
                            <TrendingUp size={14} className="text-emerald-200" />
                        </div>
                        <p className="text-sm sm:text-2xl font-extrabold">{completionRate}%</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-2.5 sm:p-4 text-white shadow-2xs">
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wide text-blue-100">Avg Exam Score</span>
                            <Star size={14} className="text-blue-200" />
                        </div>
                        <p className="text-sm sm:text-2xl font-extrabold">{averageScore}%</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-600 to-pink-700 rounded-xl p-2.5 sm:p-4 text-white shadow-2xs">
                        <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[9px] sm:text-xs font-bold uppercase tracking-wide text-purple-100">This Month</span>
                            <Calendar size={14} className="text-purple-200" />
                        </div>
                        <p className="text-sm sm:text-2xl font-extrabold">{earnedThisMonth}</p>
                    </div>
                </div>

                {/* Filter Pills */}
                {completedCourses.length > 0 && (
                    <div className="bg-white rounded-lg shadow-2xs border border-gray-100 p-1 inline-flex gap-1">
                        {['all', 'recent', 'popular'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-2.5 py-1 rounded-md text-[10px] sm:text-xs font-bold transition-all cursor-pointer ${filter === f
                                    ? 'bg-emerald-600 text-white shadow-2xs'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                {f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                    </div>
                )}

                {/* Certificates List / Empty State */}
                {completedCourses.length === 0 ? (
                    <div className="bg-white rounded-xl p-4 sm:p-10 text-center shadow-2xs border border-gray-100 space-y-2.5 max-w-md mx-auto my-3">
                        <div className="w-10 h-10 sm:w-14 sm:h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                            <Award size={20} className="sm:hidden" />
                            <Award size={28} className="hidden sm:block" />
                        </div>
                        <div>
                            <h3 className="text-xs sm:text-base font-bold text-gray-900">No Certificates Earned Yet</h3>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 leading-relaxed">
                                Complete your enrolled course videos and pass the assessment exam to generate your official certificate.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/student/mycourses')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 sm:px-6 sm:py-2.5 rounded-lg text-xs font-bold transition shadow-2xs cursor-pointer inline-flex items-center gap-1"
                        >
                            View My Courses
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
                        {completedCourses.map((course) => (
                            <div
                                key={course.id}
                                className="bg-white rounded-xl overflow-hidden shadow-2xs border border-gray-100 flex flex-col justify-between"
                            >
                                <div>
                                    <div className="relative h-32 sm:h-44">
                                        <ImageWithFallback
                                            src={course.image}
                                            alt={course.title}
                                            className="w-full h-full object-cover"
                                        />
                                        {course.certificate_earned ? (
                                            <div className="absolute top-2 right-2 bg-emerald-600 text-white px-2 py-0.5 rounded-full text-[9px] font-extrabold shadow-2xs flex items-center gap-1">
                                                <CheckCircle size={10} /> Verified
                                            </div>
                                        ) : (
                                            <div className="absolute top-2 right-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-[9px] font-extrabold shadow-2xs flex items-center gap-1">
                                                <Award size={10} /> Exam Pending
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-2.5 sm:p-4 space-y-1.5">
                                        <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-2 leading-snug">{course.title}</h3>
                                        <p className="text-[10px] sm:text-xs text-gray-500 font-medium">{course.instructor || 'Specialist Trainer'}</p>

                                        <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                            <Star size={12} className="text-amber-400 fill-current" />
                                            <span className="font-bold text-gray-700">4.9</span>
                                            <span>•</span>
                                            <span>Completed</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-2.5 sm:p-4 pt-0 space-y-1.5">
                                    {course.certificate_earned ? (
                                        <div className="grid grid-cols-3 gap-1">
                                            <button
                                                onClick={() => handleViewCertificate(course)}
                                                className="flex items-center justify-center gap-1 bg-emerald-600 text-white py-1.5 rounded-lg text-xs font-bold hover:bg-emerald-700 transition cursor-pointer"
                                            >
                                                <Eye size={12} /> View
                                            </button>
                                            <button
                                                onClick={() => handleDownloadCertificate(course)}
                                                className="flex items-center justify-center gap-1 bg-blue-600 text-white py-1.5 rounded-lg text-xs font-bold hover:bg-blue-700 transition cursor-pointer"
                                            >
                                                <Download size={12} /> Save
                                            </button>
                                            <button
                                                onClick={() => handleShareCertificate(course)}
                                                className="flex items-center justify-center gap-1 bg-purple-600 text-white py-1.5 rounded-lg text-xs font-bold hover:bg-purple-700 transition cursor-pointer"
                                            >
                                                <Share2 size={12} /> Share
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => handleStartExam(course)}
                                            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-2 rounded-lg text-xs font-extrabold transition shadow-2xs flex items-center justify-center gap-1 cursor-pointer"
                                        >
                                            <Award size={14} /> Take Exam to Unlock
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleViewAttempts(course)}
                                        className="w-full text-[9px] text-gray-500 hover:text-emerald-700 py-0.5 font-bold uppercase tracking-wider text-center flex items-center justify-center gap-1 cursor-pointer"
                                    >
                                        <Clock size={11} /> Attempts History
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Certificate Viewer Modal */}
                <CertificateModal
                    isOpen={selectedCertificate !== null && !showShareModal}
                    onClose={() => setSelectedCertificate(null)}
                    studentName={user?.name}
                    courseTitle={selectedCertificate?.title}
                    instructor={selectedCertificate?.instructor}
                    completionDate={selectedCertificate?.completedAt}
                    courseId={selectedCertificate?.id || selectedCertificate?.course_id}
                    examScore={selectedCertificate?.exam_score}
                    enrolledAt={selectedCertificate?.enrolled_at}
                    studentPhoto={user?.image}
                />

                {/* Exam Modal */}
                {examCourse && (
                    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-2.5" onClick={() => !examSubmitting && setExamCourse(null)}>
                        <div className="bg-white rounded-xl p-3.5 sm:p-6 max-w-xl w-full relative space-y-3" onClick={e => e.stopPropagation()}>
                            {!examSubmitting && (
                                <button
                                    onClick={() => setExamCourse(null)}
                                    className="absolute top-2.5 right-2.5 text-gray-400 hover:text-gray-600"
                                >
                                    <X size={18} />
                                </button>
                            )}

                            {examLoading ? (
                                <div className="text-center py-6 space-y-1.5">
                                    <div className="animate-spin rounded-full h-7 w-7 border-2 border-emerald-600 border-t-transparent mx-auto"></div>
                                    <p className="text-[11px] text-gray-600 font-medium">Generating questions using Gemini AI...</p>
                                </div>
                            ) : examResult ? (
                                <div className="text-center py-3 space-y-3">
                                    {examResult.passed ? (
                                        <>
                                            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                                                <Award size={28} />
                                            </div>
                                            <h2 className="text-base font-black text-emerald-600">Congratulations! 🎉</h2>
                                            <p className="text-xs text-gray-700">
                                                You passed the exam with a score of <strong>{examResult.score}%</strong>!
                                            </p>
                                            <button
                                                onClick={() => setExamCourse(null)}
                                                className="w-full bg-emerald-600 text-white py-2 rounded-lg font-bold text-xs"
                                            >
                                                Close & View Certificate
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <h2 className="text-base font-black text-red-600">Keep Learning!</h2>
                                            <p className="text-xs text-gray-700">
                                                You scored <strong>{examResult.score}%</strong>. Minimum passing score is 60%.
                                            </p>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleStartExam(examCourse)}
                                                    className="flex-1 bg-amber-500 text-white py-2 rounded-lg font-bold text-xs"
                                                >
                                                    Retake Exam
                                                </button>
                                                <button
                                                    onClick={() => setExamCourse(null)}
                                                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg font-bold text-xs"
                                                >
                                                    Close
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div>
                                        <h3 className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest">{examCourse.title}</h3>
                                        <h2 className="text-xs sm:text-sm font-bold text-gray-900">Assessment Exam</h2>
                                    </div>

                                    {examQuestions.length > 0 && (
                                        <div className="space-y-2.5">
                                            <div className="flex justify-between items-center text-[10px] text-gray-500 font-semibold">
                                                <span>Question {currentQuestionIndex + 1} of {examQuestions.length}</span>
                                                <span>Passing score: 60%</span>
                                            </div>

                                            <p className="text-xs font-bold text-gray-800 leading-relaxed">
                                                {examQuestions[currentQuestionIndex].question}
                                            </p>

                                            <div className="space-y-1.5">
                                                {examQuestions[currentQuestionIndex].options.map((option, optIdx) => {
                                                    const isSelected = selectedAnswers[currentQuestionIndex] === optIdx;
                                                    return (
                                                        <button
                                                            key={optIdx}
                                                            type="button"
                                                            onClick={() => setSelectedAnswers(prev => ({ ...prev, [currentQuestionIndex]: optIdx }))}
                                                            className={`w-full text-left p-2.5 rounded-lg border text-xs font-medium transition flex items-center justify-between ${isSelected
                                                                ? 'border-emerald-600 bg-emerald-50 text-emerald-800 font-bold'
                                                                : 'border-gray-200 bg-gray-50/50 text-gray-700'
                                                                }`}
                                                        >
                                                            <span>{option}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className="flex justify-between gap-2 pt-2 border-t border-gray-100">
                                                <button
                                                    type="button"
                                                    disabled={currentQuestionIndex === 0}
                                                    onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                                                    className="px-3.5 py-1.5 border border-gray-300 text-gray-700 font-bold rounded-lg text-xs disabled:opacity-40"
                                                >
                                                    Previous
                                                </button>

                                                {currentQuestionIndex < examQuestions.length - 1 ? (
                                                    <button
                                                        type="button"
                                                        disabled={selectedAnswers[currentQuestionIndex] === undefined}
                                                        onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                                                        className="px-4 py-1.5 bg-emerald-600 text-white font-bold rounded-lg text-xs disabled:opacity-40"
                                                    >
                                                        Next Question
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        disabled={examSubmitting || selectedAnswers[currentQuestionIndex] === undefined}
                                                        onClick={handleSubmitExam}
                                                        className="px-4 py-1.5 bg-emerald-600 text-white font-bold rounded-lg text-xs disabled:opacity-40"
                                                    >
                                                        {examSubmitting ? 'Submitting...' : 'Submit Exam'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default Certificates;